/**!
 *  Overlay dialog view.
 *
 *  @param string className - Append a class name.
 *  @param boolean disabled - Whether the overlay dialog should be disabled.
 *  @param string id - The overlay id.
 *  @param function onClose - Callback for when the overlay dialog is closed.
 *  @param boolean overlayClose - Whether the dialog can be closed by clicking thhe overlay bg.
 *  @param string title - Overlay dialog title.
 * 
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React, {use, useEffect, useRef} from 'react'
import styles from './overlay.module.scss'

import IconButton from 'Components/UI/IconButton'
import ScrollView from 'Components/UI/ScrollView'

export const Overlay = ({
    children,
    className = '',
    disabled,
    id,
    onClose: onCloseCallback = () => {},
    overlayClose = true,
    title = '',
    width = 340,
    viewport = null
}) => {
    const content = useRef(null)
    const dialog = useRef(null)
    const overlay = useRef(null)

    useEffect(() => {
        if (!viewport || !content.current || !dialog.current || !overlay.current)
        {
            return
        }
        const {height, y1, y2} = viewport
        const correctY = (y1 * -1 + y2) * 0.5
        dialog.current.style.transform = `translateY(${correctY}px)`
        content.current.style.maxHeight = `${height-200}px`
    }, [viewport])

    /**
     * Callback for when the close button icon is clicked.
     * @param object e - The click event.
     * @return void
     */

    const onClose = (e) =>
    {
        e?.stopPropagation()
        if (disabled)
        {
            return
        }
        onCloseCallback(e, id)
    }

    return (
        <div
            className={[
                styles.overlay,
                disabled && styles.disabled,
                className
            ].filter(c => !!c).join(' ')}
            onClick={overlayClose ? onClose : null}
            ref={overlay}
        >
            <div
                className={styles.dialog}
                onClick={e => e.stopPropagation()}
                style={{maxWidth: width}}
            >
                <div className={styles.align} ref={dialog}>
                    <h2 className={styles.title}>{ title }</h2>
                    <IconButton
                        className={styles.close}
                        feather='X'
                        onClick={onClose}
                    />
                    <div
                        className={styles.content}
                        ref={content}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Overlay